import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SmartToy, Send, Refresh as RefreshIcon, PersonOutlineOutlined, ContentCopy, History as HistoryIcon, AutoAwesome as AutoAwesomeIcon, Psychology as PsychologyIcon, Analytics as AnalyticsIcon, Build as BuildIcon, Create as CreateIcon, Construction as ConstructionIcon } from '@mui/icons-material';
import { ChatPanelProps } from '../../types';
import logo from 'app/images/IQ-Transparent.png';
import MarkupMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/MarkupMAIQ';

const ChatContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: 'white',
  borderRight: `1px solid ${theme.palette.divider}`, // Keep only right border
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  boxShadow: 'none', // Remove shadow
  border: 'none', // Remove all borders except right
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: theme.palette.secondary.main,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2, 2),
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: '28px',
  height: '28px',
  marginRight: theme.spacing(1.5),
}));

const ChatMessages = styled(Box)(({ theme }) => ({
  flex: "1 1 auto",
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
  // boxSizing: 'border-box',
  height: '100%',
}));

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
  marginRight: theme.spacing(2), // Add right spacing
}));

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  flexDirection: 'row',
  maxWidth: 'calc(100% - 60px)',
  minWidth: 0,
  boxSizing: 'border-box',
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => !['isUser', 'processingStatus'].includes(prop as string),
})<{ isUser: boolean; processingStatus?: string }>(({ theme, isUser, processingStatus }) => {
  // Define colors based on processing status
  let backgroundColor = isUser ? theme.palette.secondary.main : '#f5f5f5';
  let borderColor = 'transparent';
  let textColor = isUser ? 'white' : '#1f2937';
  let animation = 'none';
  
  // Change colors for AI messages with processing status - use same style for all
  if (!isUser && processingStatus && processingStatus !== 'idle') {
    // Use consistent styling for all processing types
    backgroundColor = '#fff3cd'; // Light yellow - same as summarizing_history
    borderColor = '#ffc107';
    textColor = '#856404';
    animation = 'pulse 2s infinite';
  }

  return {
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    maxWidth: 'calc(100% - 120px)', // More conservative spacing
    minWidth: 'fit-content',
    width: 'fit-content',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    hyphens: 'none',
    backgroundColor,
    color: textColor,
    fontSize: '14px',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    boxSizing: 'border-box',
    display: 'inline-block',
    position: 'relative',
    border: borderColor !== 'transparent' ? `2px solid ${borderColor}` : 'none',
    transition: 'all 0.3s ease',
    animation,
    // Enhanced styling for MarkupMAIQ content
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      margin: '8px 0',
      paddingLeft: '20px',
    },
    '& li': {
      margin: '4px 0',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 8px 0',
      fontWeight: 600,
    },
    '& code': {
      backgroundColor: isUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '13px',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      padding: '8px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '8px 0',
    },
    '& blockquote': {
      borderLeft: `3px solid ${isUser ? 'rgba(255, 255, 255, 0.5)' : '#ddd'}`,
      margin: '8px 0',
      paddingLeft: '12px',
      fontStyle: 'italic',
    },
    '@keyframes pulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${borderColor}40`,
      },
      '70%': {
        boxShadow: `0 0 0 10px ${borderColor}00`,
      },
      '100%': {
        boxShadow: `0 0 0 0 ${borderColor}00`,
      },
    },
  };
});

const OptionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(5.5), // Align with AI message
  maxWidth: '100%',
}));

const OptionButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  textTransform: 'none',
  fontSize: '12px',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
}));

const ChatInput = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  gap: theme.spacing(1),
}));

const InputField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const SendButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: 'white',
  width: '40px',
  height: '40px',
  minWidth: '40px',
  minHeight: '40px',
  maxWidth: '40px',
  maxHeight: '40px',
  padding: 0,
  borderRadius: '50%',
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
    boxShadow: '0 2px 8px rgba(44,181,67,0.10)',
  },
  '&:active': {
    backgroundColor: theme.palette.secondary.dark,
    boxShadow: '0 1px 4px rgba(44,181,67,0.15)',
  },
  '&:disabled': {
    backgroundColor: '#d1d5db',
    color: '#9ca3af',
  },
  transition: 'background 0.2s, box-shadow 0.2s',
}));

const CopyIcon = styled(IconButton)(({ theme }) => ({
  width: '20px',
  height: '20px',
  minWidth: '20px',
  minHeight: '20px',
  backgroundColor: 'rgb(193, 193, 193)',
  color: 'rgb(96, 96, 96)',
  borderRadius: '50%',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
  opacity: 0,
  transition: 'all 0.2s ease-in-out',
  position: 'absolute',
  top: '8px',
  right: '8px',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgb(175, 175, 175)',
    transform: 'scale(1.05)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.19)',
  },
}));

const MessageBubbleContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  '&:hover .copy-icon': {
    opacity: 1,
  },
}));

const CopyIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(0.5),
  marginRight: theme.spacing(1),
}));

const ChatPanel: React.FC<ChatPanelProps> = ({
  chatHistory,
  onOptionSelect,
  onSendMessage,
  inputText,
  setInputText,
  loading = false,
  onReset,
  suggestions = [],
  isInputRestricted = false,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
    // Fallback to chatEndRef scroll
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Scroll when chat history changes
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Scroll when loading state changes (when AI starts/stops responding)
  useEffect(() => {
    if (loading) {
      scrollToBottom();
    }
  }, [loading]);

  const handleSendMessage = () => {
    if (inputText.trim() && !loading && !isInputRestricted) {
      onSendMessage(inputText.trim());
      setInputText('');
      // Scroll to bottom after sending message
      setTimeout(() => {
        scrollToBottom();
      }, 50);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isInputRestricted) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyMessage = (text: string, messageIndex: number) => {
    // For MarkupMAIQ content, we need to extract plain text
    const plainText = text.replace(/<[^>]*>/g, '').replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1');
    navigator.clipboard.writeText(plainText).then(() => {
      setCopiedMessageId(messageIndex);
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 3000);
    }).catch((err) => {
      // Silent fail
    });
  };

  const getAvatarContent = (sender: 'user' | 'ai' | 'system') => {
    if (sender === 'ai') {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          <SmartToy sx={{ fontSize: 20 }} />
        </Avatar>
      );
    }
    if (sender === 'system') {
      return (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main' }}>
          <BuildIcon sx={{ fontSize: 18, color: 'white' }} />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
        <PersonOutlineOutlined sx={{ fontSize: 20 }} />
      </Avatar>
    );
  };

  return (
    <ChatContainer elevation={0} sx={{
      flex: 1,
      overflow: 'auto',
    }}>
      <ChatHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LogoImage src={logo} alt="IQ Logo" />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'common.white', letterSpacing: 1 }}
          >
            Chat
          </Typography>
        </Box>
        <IconButton
          onClick={onReset}
          color="secondary"
          sx={{
            background: 'transparent',
            borderRadius: 1,
            transition: 'background 0.2s',
            '&:hover': { background: 'action.hover' },
          }}
        >
          <Tooltip title="Reset Chat" arrow>
            <RefreshIcon sx={{ color: 'common.white' }} />
          </Tooltip>
        </IconButton>
      </ChatHeader>

      <ChatMessages ref={chatMessagesRef}>
        {chatHistory.map((msg, index) => (
          <MessageContainer key={msg.id || index} isUser={msg.role === 'user'}>
            <MessageRow isUser={msg.role === 'user'}>
              {getAvatarContent(msg.role === 'user' ? 'user' : msg.role === 'system' ? 'system' : 'ai')}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <MessageBubbleContainer>
                  <MessageBubble
                    isUser={msg.role === 'user'}
                    processingStatus={msg.processingType}
                    sx={msg.bgcolor ? { backgroundColor: msg.bgcolor } : {}}
                  >
                    {msg.role === 'assistant' ? (
                      <MarkupMAIQ content={msg.content} />
                    ) : (
                      msg.content
                    )}
                  </MessageBubble>
                  
                  {/* Copy Icon positioned at top right of message bubble */}
                  {msg.role === 'assistant' && (
                    <CopyIcon
                      className="copy-icon"
                      onClick={() => handleCopyMessage(msg.content, index)}
                      size="small"
                      aria-label="Copy message"
                      sx={{
                        color: 'white',
                      }}
                    >
                      {copiedMessageId === index ? (
                        <Box sx={{ color: 'success.main', fontSize: 12 }}>✓</Box>
                      ) : (
                        <ContentCopy sx={{ fontSize: 12 }} />
                      )}
                    </CopyIcon>
                  )}
                </MessageBubbleContainer>
              </Box>
            </MessageRow>
            {/* Show suggestions from API response for the last message */}
            {index === chatHistory.length - 1 && suggestions.length > 0 && (
              <OptionsContainer>
                {suggestions.map((suggestion) => (
                  <OptionButton
                    key={suggestion.id}
                    onClick={() => onOptionSelect(suggestion)}
                    disabled={loading || isInputRestricted}
                  >
                    {suggestion.text}
                  </OptionButton>
                ))}
              </OptionsContainer>
            )}
          </MessageContainer>
        ))}
        {loading && (
          <MessageContainer isUser={false}>
            <MessageRow isUser={false}>
              {getAvatarContent('ai')}
              <Box sx={{ p: 2 }}>
                <CircularProgress size={20} sx={{ color: 'primary.main' }} />
              </Box>
            </MessageRow>
          </MessageContainer>
        )}
        <div ref={chatEndRef} />
      </ChatMessages>

      <ChatInput>
        <InputField
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isInputRestricted ? "Please select from the suggestions above..." : "Type your message here..."}
          disabled={loading || isInputRestricted}
          size="small"
          multiline
          maxRows={3}
        />
        <SendButton
          onClick={handleSendMessage}
          disabled={!inputText.trim() || loading || isInputRestricted}
          aria-label="Send"
        >
          <Send sx={{ fontSize: 22 }} />
        </SendButton>
      </ChatInput>
    </ChatContainer>
  );
};

export default ChatPanel;
